import { useEffect, useRef } from 'react';

export interface WaitForStateConditionParams {
  state: any;
  condition?: (state: any) => boolean;
  interval?: number;
  timeout?: number;
  rejectMessage?: string;
}

/**
 * Hook to wait for a condition to be met on a state variable.
 * @param state {any} The state variable to watch.
 * @param [condition] {Function} The condition to wait for. The function gets
 * the current value of the state and should return true if the condition is met.
 * Defaults to a simple truthy check.
 * @param [interval] {number} The interval in milliseconds to check the condition.
 * Defaults to 100.
 * @param [timeout] {number} The timeout in milliseconds to wait for the condition.
 * If the timeout is reached, the promise will be rejected. Defaults to 10_000.
 * @param [rejectMessage] {string} The message to reject the with in case of timeout.
 * @returns {Function} A function that returns a promise that resolves when the
 * condition is met or rejects when the timeout is reached.
 * @example
 * const [myState, setMyState] = useState(0);
 * const waitForMyState = useWaitForStateCondition({
 *   myState,
 *   condition: (myState) => myState === 1
 * });
 * // ...
 * await waitForMyState();
 */
export const useWaitForStateCondition = ({
  state,
  condition = Boolean,
  interval = 100,
  timeout = 10000,
  rejectMessage = 'Waiting for state timed out',
}: WaitForStateConditionParams) => {
  const ref = useRef({
    isConditionMet: false,
    timePassed: 0,
  });

  // when the state or the condition changes, check the condition again
  useEffect(() => {
    if (condition(state)) {
      ref.current.isConditionMet = true;
    }
  }, [condition, state]);

  return () => {
    if (ref.current.isConditionMet) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const timerId = setInterval(() => {
        ref.current.timePassed += interval;

        if (ref.current.isConditionMet) {
          clearInterval(timerId);
          resolve(null);
        }

        if (ref.current.timePassed >= timeout) {
          clearInterval(timerId);
          reject(rejectMessage);
        }
      }, interval);
    });
  };
};
